import type { AbortReasonCode } from '@chukua/common';
import { Alert, Form, Input, Modal, Select } from 'antd';
import { useEffect } from 'react';
import { ABORT_REASON_OPTIONS } from '../policies/abort-reason-labels.policy';

interface FormValues {
  reasonCode: AbortReasonCode;
  reason: string;
}

interface AbortModalProps {
  open: boolean;
  submitting: boolean;
  error: string | null;
  onSubmit: (values: FormValues) => Promise<boolean>;
  onCancel: () => void;
}

/** The cash line is there because aborting moves money: whatever the driver was given and did not spend is taken back from their balance. */
export function AbortModal({ open, submitting, error, onSubmit, onCancel }: AbortModalProps) {
  const [form] = Form.useForm<FormValues>();

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  async function submit() {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    if (await onSubmit({ ...values, reason: values.reason.trim() })) form.resetFields();
  }

  return (
    <Modal
      open={open}
      title="Abort this trip"
      onOk={submit}
      onCancel={onCancel}
      confirmLoading={submitting}
      destroyOnHidden
      okText="Abort trip"
      okButtonProps={{ danger: true }}
    >
      <p className="mb-4 text-sm text-muted">
        The truck, trailer and driver are freed. Any cash given to the driver that was not spent is taken back from their balance. If this was the job&apos;s
        only trip, the job goes back to planning.
      </p>
      {error && <Alert type="error" showIcon message={error} className="mb-4" />}
      <Form<FormValues> form={form} layout="vertical" requiredMark={false}>
        <Form.Item name="reasonCode" label="Reason code" rules={[{ required: true, message: 'Choose why the trip is being aborted' }]}>
          <Select options={ABORT_REASON_OPTIONS} placeholder="Choose a reason" />
        </Form.Item>
        <Form.Item
          name="reason"
          label="Reason"
          rules={[
            { required: true, whitespace: true, message: 'Say what happened' },
            { max: 500, message: 'At most 500 characters' },
          ]}
        >
          <Input.TextArea rows={3} maxLength={500} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}
